import { walkParents } from '@/helpers/reports'

/**
 * Пересчитывает тотал при изменении данных в таблице. Одна и та же строка бюджета
 * без Affiliate ID может отображаться сразу под несколькими ветками Affiliate ID
 * как отдельные копии узла (см. findLeavesByBudgetId) — пересчитывать нужно цепочку
 * предков не только отредактированной ноды, но и всех остальных копий, иначе их
 * собственные ветки-родители не подхватят новое значение до перезагрузки страницы.
 * @param item - измененная нода 4 уровня
 * @param total - объект тотала
 * @param items - все элементы таблицы (roots)
 */
export function recalcBudgetPlannerData(item, total, items) {
  try {
    const leaves = findLeavesByBudgetId(items, item.budget_id)
    const nodesToRecalc = leaves.length ? leaves : [item]
    nodesToRecalc.forEach(node => {
      recalcBudgetPlannerNode(node)
      walkParents(node, parent => recalcBudgetPlannerParents(parent))
    })
    recalcBudgetPlannerTotal(total, items)
  } catch (e) {
    console.error('recalcBudgetPlannerData:', e)
  }
}

/**
 * Пересчитывает status обновленного элемента Budget Planner
 * @param node - дочерняя обновляемая нода
 */
function recalcBudgetPlannerNode(node) {
  if (!node.request_budget) node.status = 'no_request'
  else if (node.request_budget && node.approve_budget === null) node.status = 'need_approval'
  else if (node.request_budget && [0, '0'].includes(node.approve_budget)) node.status = 'not_approved'
  else if (node.request_budget && node.approve_budget) node.status = 'approved'
  updateBudgetPlannerRowData(node)
}

const GROUP_KEYS = ['sg', 'tg', 'fg', 'ffg']

/**
 * Проверяет, есть ли у ноды дочерние группы (т.е. это не листовой узел).
 * Смотрит на наличие самого массива, а не на его длину: у группового узла ключ
 * (sg/tg/fg/ffg) всегда определён как массив, даже если фильтрация вычистила
 * из него все элементы. У настоящего листа (строки бюджета) такого ключа нет
 * вовсе. Если бы проверка требовала непустой массив, узел, у которого фильтр
 * вырезал всех детей, выглядел бы как лист и его собственные (нерелевантные)
 * числовые поля задвоили бы сумму родителя.
 * @param node - нода дерева
 */
function hasChildGroups(node) {
  return GROUP_KEYS.some(key => Array.isArray(node[key]))
}

/**
 * Собирает все листовые узлы (строки с бюджетом) в поддереве ноды
 * @param node - нода дерева
 * @param acc - аккумулятор листьев
 */
export function collectLeaves(node, acc = []) {
  if (!hasChildGroups(node)) {
    acc.push(node)
    return acc
  }
  GROUP_KEYS.forEach(key => {
    if (Array.isArray(node[key])) {
      node[key].forEach(child => collectLeaves(child, acc))
    }
  })
  return acc
}

/**
 * Значение Approve для агрегированной строки (root/sg/tg/fg с детьми). Сумма 0 бывает
 * и когда решений ещё не было, и когда по строкам нажали No/ввели 0 — во втором случае
 * показываем 0, чтобы было видно, что строка уже пройдена (CPA-3511). 0 — только если у всех
 * листьев поддерева approve_budget задан (т.е. все дочерние = 0); если хотя бы у одного
 * листа прочерк — показываем прочерк.
 * @param node - агрегированная нода
 */
export function getAggregatedApproveBudget(node) {
  if (+node.approve_budget) return node.approve_budget
  const leaves = collectLeaves(node)
  const allDecided = leaves.length && leaves.every(leaf => ![null, undefined, ''].includes(leaf.approve_budget))
  return allDecided ? 0 : '-'
}

const FULL_GROUP_KEYS ={ sg: '_allSg', tg: '_allTg', fg: '_allFg', ffg: '_allFfg' }

/**
 * Собирает ВСЕ листья поддерева ноды — включая скрытые клиентским Table view/статус/
 * approve/fact/balance фильтром (applyFilters в BudgetPlanner.module.js подменяет
 * sg/tg/fg/ffg отфильтрованной выборкой, полное дерево остаётся в _allSg/_allTg/_allFg/
 * _allFfg — см. applyViewOptionFilter). Нужно для findLeavesByBudgetId: при привязке
 * общей строки к Affiliate ID (CPA-3278) скрытая на момент правки копия budget_id тоже
 * должна получить сброс, иначе после того как её снова покажут (сменой Table view,
 * другой правкой фильтра или просто раскрытием), она будет редактироваться с чужим,
 * уже перевязанным budget_id.
 * @param node - нода дерева
 * @param acc - аккумулятор листьев
 */
function collectAllLeaves(node, acc = []) {
  const hasFullChildGroups = GROUP_KEYS.some(key => Array.isArray(node[key]) || Array.isArray(node[FULL_GROUP_KEYS[key]]))
  if (!hasFullChildGroups) {
    acc.push(node)
    return acc
  }
  GROUP_KEYS.forEach(key => {
    const children = node[FULL_GROUP_KEYS[key]] ?? node[key]
    if (Array.isArray(children)) {
      children.forEach(child => collectAllLeaves(child, acc))
    }
  })
  return acc
}

/**
 * Собирает все листья дерева (по всем roots) с указанным budget_id — по полному
 * дереву (см. collectAllLeaves), а не только по видимой сейчас части. Листья без
 * budget_id (null/undefined) не считаются копиями друг друга (см. dedupeLeavesByBudgetId)
 * и в результат не попадают.
 * @param roots - корневые элементы таблицы
 * @param budgetId - budget_id искомой строки
 */
function findLeavesByBudgetId(roots, budgetId) {
  if (budgetId === null || budgetId === undefined) return []
  return roots.flatMap(root => collectAllLeaves(root)).filter(leaf => leaf.budget_id === budgetId)
}

/**
 * Проставляет новое значение поля всем копиям строки бюджета с тем же budget_id, что
 * и у отредактированной. Вызывать после успешного сохранения на бэкенде — иначе при
 * ошибке сохранения остальные копии подхватят значение, которое не подтвердилось.
 *
 * Общая (affiliate_id = NULL) строка, не утверждённая ДО этой правки, бэкендом привязывается к той
 * ветке Affiliate ID, под которой её отредактировали (PlannedBudgetController::bindAffiliateIfEligible,
 * гейт по $originalApprove — CPA-3271) — то есть после сохранения строка реально остаётся только в
 * ОДНОЙ из показанных копий. Условие привязки повторено здесь: leaves.length > 1 (строка была общей,
 * иначе бэкенду нечего привязывать), affiliateId у отредактированной ветки задан, и approve_budget
 * ДО этой правки был пуст (иначе бэкенд привязку не делает — см. коммент в PlannedBudgetController).
 * Важно брать именно originalApproveBudget, а не текущий editedLeaf.approve_budget: при правке
 * самого approve_budget вызывающий код (ListBudgetPlanner.vue) успевает проставить в ноду новое
 * значение до этого вызова, и оно почти никогда не null — проверка по нему всегда давала false и
 * не давала сработать привязке (CPA-3280). Остальным копиям, которые бэкенд только что отвязал,
 * нужно сбросить budget_id/суммы, иначе они будут показывать чужое значение до перезагрузки страницы.
 * @param roots - корневые элементы таблицы
 * @param budgetId - budget_id отредактированной строки
 * @param field - изменённое поле (request_budget/approve_budget)
 * @param value - новое значение поля
 * @param editedLeaf - нода, которую редактировал пользователь
 * @param affiliateId - affiliate_id_id ветки редактируемой ноды (см. getAffiliateId в ListBudgetPlanner.vue)
 * @param originalApproveBudget - approve_budget редактируемой ноды ДО этой правки
 */
export function syncBudgetLeavesByBudgetId(roots, budgetId, field, value, editedLeaf, affiliateId, originalApproveBudget) {
  const leaves = findLeavesByBudgetId(roots, budgetId)
  const bindsToAffiliate = leaves.length > 1 && affiliateId != null && originalApproveBudget === null

  leaves.forEach(leaf => {
    if (leaf === editedLeaf || !bindsToAffiliate) {
      leaf[field] = value
      return
    }
    leaf.budget_id = null
    leaf.request_budget = null
    leaf.approve_budget = null
    recalcBudgetPlannerNode(leaf)
    walkParents(leaf, parent => recalcBudgetPlannerParents(parent))
  })
}

/**
 * Убирает дубли строк бюджета: одна и та же budget_id может быть показана
 * сразу в нескольких ветках (например, без Affiliate ID — во всех ветках Affiliate ID).
 * Листья без budget_id (null/undefined) не сливаются друг с другом.
 * @param leaves - список листовых узлов
 */
function dedupeLeavesByBudgetId(leaves) {
  const seen = new Set()
  return leaves.filter(leaf => {
    const budgetId = leaf.budget_id
    if (budgetId === null || budgetId === undefined) return true
    if (seen.has(budgetId)) return false
    seen.add(budgetId)
    return true
  })
}

/**
 * Суммирует request_budget/approve_budget/current_fact/spend_1..3 по списку листьев.
 * balance в сумму не входит — он пересчитывается из уже просуммированных
 * approve_budget и current_fact в updateBudgetPlannerRowData, иначе он считался бы
 * по смешанным (частично отфильтрованным, частично полным) данным.
 * @param leaves - список листовых узлов
 */
function sumLeaves(leaves) {
  return leaves.reduce(
    (acc, cur) => {
      acc.request_budget += +cur.request_budget || 0
      acc.approve_budget += +cur.approve_budget || 0
      acc.current_fact += +cur.current_fact || 0
      acc.spend_1 += +cur.spend_1 || 0
      acc.spend_2 += +cur.spend_2 || 0
      acc.spend_3 += +cur.spend_3 || 0
      return acc
    },
    { request_budget: 0, approve_budget: 0, current_fact: 0, spend_1: 0, spend_2: 0, spend_3: 0 },
  )
}

/**
 * Пересчитывает суммы родительской ноды (fg/tg/sg/root) по её листьям,
 * с дедупликацией по budget_id (см. dedupeLeavesByBudgetId)
 * @param node - родительская нода
 */
export function recalcBudgetPlannerParents(node) {
  try {
    if (!hasChildGroups(node)) return
    const leaves = dedupeLeavesByBudgetId(collectLeaves(node))
    Object.assign(node, sumLeaves(leaves))
    updateBudgetPlannerRowData(node)
  } catch (e) {
    console.error('recalcBudgetPlannerParents:', e)
  }
}

/**
 * Пересчитывает тотал по всем корневым нодам, с дедупликацией по budget_id
 * @param total - объект тотала
 * @param roots - корневые элементы таблицы
 */
export function recalcBudgetPlannerTotal(total, roots) {
  try {
    const leaves = dedupeLeavesByBudgetId(roots.flatMap(root => collectLeaves(root)))
    Object.assign(total, sumLeaves(leaves))
    updateBudgetPlannerRowData(total)
  } catch (e) {
    console.error('recalcBudgetPlannerTotal:', e)
  }
}

/**
 * Пересчитывает баланс ряда Budget Planner
 * @param node - текущий ряд
 */
function updateBudgetPlannerRowData(node) {
  node.balance = node.approve_budget - node.current_fact
}

/**
 * Пересчитывает статусы листовых узлов и суммы родителей (fg/tg/sg/root) и общий
 * тотал после клиентской фильтрации, чтобы они соответствовали оставшимся строкам.
 * Суммирование идёт по уникальным budget_id (см. dedupeLeavesByBudgetId), а не по
 * простой сумме детей: одна и та же строка бюджета без Affiliate ID может быть
 * продублирована бэкендом сразу в нескольких ветках Affiliate ID для отображения,
 * и наивная сумма детей задвоила бы итоги.
 * @param roots - корневые элементы таблицы (уже отфильтрованные)
 * @param total - объект тотала
 */
export function recalcAfterFilter(roots, total) {
  roots.forEach(root => {
    root.sg.forEach(sg => {
      sg.tg.forEach(tg => {
        if (tg.fg && tg.fg.length) {
          tg.fg.forEach(fg => {
            if (fg.ffg && fg.ffg.length) {
              fg.ffg.forEach(ffg => {
                recalcBudgetPlannerNode(ffg)
              })
              recalcBudgetPlannerParents(fg)
            } else {
              recalcBudgetPlannerNode(fg)
            }
          })
          recalcBudgetPlannerParents(tg)
        }
      })
      recalcBudgetPlannerParents(sg)
    })
    recalcBudgetPlannerParents(root)
  })
  if (total) recalcBudgetPlannerTotal(total, roots)
}